import * as Sentry from '@sentry/react';

import { PlatformBridge } from './bridge';

export interface NetworkInterface {
  name: string;
  address: string;
  netmask: string;
  family: 'IPv4' | 'IPv6';
  mac: string;
  internal: boolean;
  cidr: string | null;
}

export interface NetworkInfo {
  interfaces: NetworkInterface[];
}

export interface NetworkInfoBridge {
  getNetworkInterfaces(): Promise<{ interfaces: NetworkInterface[] }>;
}

function getCapacitorNetworkInfo(): NetworkInfoBridge | null {
  const plugins = PlatformBridge.capacitorPlugins;
  if (!plugins) return null;
  return (plugins.NetworkInfo as NetworkInfoBridge) ?? null;
}

async function getWebNetworkInfo(): Promise<NetworkInfo> {
  // En environnement web pur, on a des infos très limitées
  const interfaces: NetworkInterface[] = [];

  // On peut essayer d'utiliser WebRTC pour obtenir l'IP locale
  try {
    const pc = new RTCPeerConnection({ iceServers: [] });
    pc.createDataChannel('');

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    // Attendre un peu pour que les candidats ICE soient générés
    await new Promise<void>(resolve => {
      let resolved = false;
      pc.onicecandidate = ice => {
        if (!ice || !ice.candidate || !ice.candidate.candidate) {
          if (!resolved) {
            resolved = true;
            resolve();
          }
          return;
        }

        const candidate = ice.candidate.candidate;
        const parts = candidate.split(' ');

        if (parts.length > 4) {
          const ip = parts[4];
          const isIPv6 = ip.includes(':');

          // Éviter les doublons
          if (!interfaces.some(iface => iface.address === ip)) {
            interfaces.push({
              name: 'unknown',
              address: ip,
              netmask: 'unknown',
              family: isIPv6 ? 'IPv6' : 'IPv4',
              mac: 'unknown',
              internal: false,
              cidr: null,
            });
          }
        }
      };

      // Timeout après 2 secondes
      setTimeout(() => {
        if (!resolved) {
          resolved = true;
          resolve();
        }
      }, 2000);
    });

    pc.close();
  } catch (error) {
    console.error('Error getting network info from WebRTC:', error);
    Sentry.captureException(error);
  }

  return {
    interfaces,
  };
}

export const NetworkBridge = {
  getNetworkInfoBridge(): NetworkInfoBridge | null {
    return getCapacitorNetworkInfo();
  },

  async getNetworkInfo(): Promise<NetworkInfo> {
    const bridge = NetworkBridge.getNetworkInfoBridge();

    if (bridge) {
      try {
        const result = await bridge.getNetworkInterfaces();
        return {
          interfaces: result.interfaces,
        };
      } catch (error) {
        console.error('Error getting network info from bridge:', error);
        Sentry.captureException(error);
        return {
          interfaces: [],
        };
      }
    }

    // Fallback to web implementation
    return getWebNetworkInfo();
  },
};
